/**
 * 헌혈요청 카드/상세 표기용 포맷터.
 * 디자인(피플 웹 채팅 디자인 시스템)의 뱃지·팩트 표기를 백엔드 값에서 만들어낸다.
 */

/**
 * 혈액형 표기: {abo:'O', rh:'POSITIVE'} → "O형 Rh+"
 *
 * 주의: 백엔드 enum은 POSITIVE(정상 철자)인데
 * src/interfaces/community.ts 의 타입은 'POSTIVE'(오타)로 선언돼 있다.
 * 실제 응답이 어느 쪽이든 깨지지 않도록 둘 다 양성으로 취급한다.
 */
export const formatBloodType = (bloodType?: {
  abo?: string;
  rh?: string;
}): string => {
  const abo = bloodType?.abo;
  if (!abo) return '';
  const rh = bloodType?.rh;
  if (!rh) return `${abo}형`;
  const isPositive = rh === 'POSITIVE' || rh === 'POSTIVE';
  return `${abo}형 Rh${isPositive ? '+' : '-'}`;
};

/** 혈액 성분: enum → 한글 라벨 */
export const formatBloodProduct = (bloodProduct?: string): string => {
  switch (bloodProduct) {
    case 'WHOLE':
      return '전혈';
    case 'PLATELET':
      return '혈소판';
    case 'LEUKOCYTE':
      return '백혈구';
    case 'PLATELET_PLASMA':
      return '혈소판혈장';
    case 'PLASMA':
      return '혈장';
    default:
      return bloodProduct ?? '';
  }
};

/**
 * 마감까지 남은 정도. deadlineAt이 없으면 null(디자인에서 뱃지 자체를 숨긴다).
 * 지난 경우 '마감', 오늘이면 'D-day'.
 */
export const formatDeadline = (
  deadlineAt?: string | null
): { label: string; urgent: boolean } | null => {
  if (!deadlineAt) return null;
  const target = new Date(deadlineAt);
  if (Number.isNaN(target.getTime())) return null;

  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return { label: '마감', urgent: false };

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return { label: 'D-day', urgent: true };
  // 3일 이내면 긴급으로 본다(백엔드에 별도 긴급 플래그가 없어 마감 기준으로 파생).
  return { label: `D-${days}`, urgent: days <= 3 };
};

/** 목록 카드용 마감 표기: "8/14 마감" (디자인 Blood Request List) */
export const formatDeadlineShort = (deadlineAt?: string | null): string => {
  if (!deadlineAt) return '';
  const d = new Date(deadlineAt);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()} 마감`;
};

/** 상세 팩트용 마감 표기: "8월 14일 오전" (디자인 Blood Request) */
export const formatDeadlineLong = (deadlineAt?: string | null): string => {
  if (!deadlineAt) return '';
  const d = new Date(deadlineAt);
  if (Number.isNaN(d.getTime())) return '';
  const half = d.getHours() < 12 ? '오전' : '오후';
  return `${d.getMonth() + 1}월 ${d.getDate()}일 ${half}`;
};

/** "10분 전" 형태의 상대 시간 */
export const formatRelativeTime = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return '방금 전';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}분 전`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}시간 전`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}일 전`;
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

/** 진행 상태: ACTIVE→모집중, COMPLETE→완료 (그 외는 표시하지 않음) */
export const formatDonationStatus = (
  status?: string
): { label: string; done: boolean } | null => {
  if (status === 'ACTIVE') return { label: '모집중', done: false };
  if (status === 'COMPLETE') return { label: '완료', done: true };
  return null;
};
